"use client";

import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  CardMedia,
  Button,
  Snackbar,
  IconButton,
  TextField,
  Card,
  CardContent,
  Divider,
  Chip,
  Alert,
  Container,
  Skeleton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import SecurityIcon from "@mui/icons-material/Security";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { useNavigate } from "react-router-dom";

const CartCom = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCartItems = async () => {
      const cart = JSON.parse(localStorage.getItem("cart")) || [];

      try {
        const medicinePromises = cart.map(({ id, quantity }) =>
          fetch(
            `https://namami-infotech.com/EvaraBackend/src/sku/get_sku.php?medicine_id=${id}`
          )
            .then((response) => response.json())
            .then((data) => {
              if (data.message === "Medicine details fetched successfully") {
                return { ...data.medicine, quantity };
              }
              return null;
            })
        );

        const medicineData = await Promise.all(medicinePromises);
        const medicinesInCart = medicineData.filter(Boolean);
        setCartItems(medicinesInCart);
      } catch (err) {
        setError("An error occurred while fetching data.");
      } finally {
        setLoading(false);
      }
    };

    fetchCartItems();
  }, []);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleQuantityChange = (medicineId, quantity) => {
    const updatedCart = cartItems.map((item) => {
      if (item.medicine_id === medicineId) {
        return { ...item, quantity: Math.max(1, Math.min(quantity, 999)) };
      }
      return item;
    });
    setCartItems(updatedCart);
    localStorage.setItem(
      "cart",
      JSON.stringify(
        updatedCart.map((item) => ({
          id: item.medicine_id,
          quantity: item.quantity,
        }))
      )
    );
  };

  const handleRemove = (medicineId) => {
    const updatedCart = cartItems.filter(
      (item) => item.medicine_id !== medicineId
    );
    setCartItems(updatedCart);
    localStorage.setItem(
      "cart",
      JSON.stringify(
        updatedCart.map((item) => ({
          id: item.medicine_id,
          quantity: item.quantity,
        }))
      )
    );
    setSnackbarMessage(`Removed item from cart!`);
    setSnackbarOpen(true);
  };

  const calculateSubtotal = () => {
    return cartItems.reduce(
      (total, item) => total + item.quantity * item.ptr,
      0
    );
  };

  const calculateTax = () => {
    return calculateSubtotal() * 0.18; // 18% GST
  };

  const calculateTotal = () => {
    return (calculateSubtotal() + calculateTax()).toFixed(2);
  };

  const calculateSavings = () => {
    return cartItems.reduce((savings, item) => {
      const mrpTotal = item.quantity * item.mrp;
      const ptrTotal = item.quantity * item.ptr;
      return savings + (mrpTotal - ptrTotal);
    }, 0);
  };

  const handleCheckout = () => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    if (userData && userData.user_id) {
      navigate("/checkout");
    } else {
      setSnackbarMessage("Please log in to proceed with checkout.");
      setSnackbarOpen(true);
      navigate("/login");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #008080 0%, #20B2AA 50%, #48D1CC 100%)",
          py: 4,
        }}
      >
        <Container maxWidth="xl">
          <Typography variant="h4" gutterBottom sx={{ color: "white", mb: 4 }}>
            Shopping Cart
          </Typography>
          <Grid container spacing={3}>
            {[1, 2, 3].map((item) => (
              <Grid item xs={12} md={8} key={item}>
                <Card sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Skeleton variant="rectangular" width={100} height={100} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="60%" height={30} />
                      <Skeleton variant="text" width="40%" height={20} />
                      <Skeleton variant="text" width="50%" height={20} />
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #008080 0%, #20B2AA 50%, #48D1CC 100%)",
          py: 4,
        }}
      >
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ mt: 4 }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #008080 0%, #20B2AA 50%, #48D1CC 100%)",
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              color: "white",
              fontWeight: 700,
              textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <ShoppingCartIcon sx={{ fontSize: 40 }} />
            Shopping Cart ({cartItems.length} items)
          </Typography>
        </Box>

        {cartItems.length === 0 ? (
          // Empty Cart State
          <Card
            sx={{
              p: 6,
              textAlign: "center",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            }}
          >
            <ShoppingCartIcon sx={{ fontSize: 80, color: "#008080", mb: 2 }} />
            <Typography
              variant="h5"
              gutterBottom
              sx={{ color: "#008080", fontWeight: 600 }}
            >
              Your cart is empty
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Looks like you haven't added any items to your cart yet.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/")}
              sx={{
                background: "linear-gradient(45deg, #008080, #20B2AA)",
                px: 4,
                py: 1.5,
                borderRadius: 2,
                "&:hover": {
                  background: "linear-gradient(45deg, #006666, #1a9999)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(0, 128, 128, 0.4)",
                },
              }}
            >
              Continue Shopping
            </Button>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {/* Cart Items */}
            <Grid item xs={12} md={8}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {cartItems.map((medicine, index) => (
                  <Card
                    key={medicine.medicine_id}
                    sx={{
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(10px)",
                      borderRadius: 3,
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      animation: `fadeIn 0.6s ease ${index * 0.1}s both`,
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 40px rgba(0, 128, 128, 0.2)",
                      },
                      "@keyframes fadeIn": {
                        from: { opacity: 0, transform: "translateY(20px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 3 }}
                      >
                        {/* Product Image */}
                        <Box
                          sx={{
                            position: "relative",
                            borderRadius: 2,
                            overflow: "hidden",
                            background:
                              "linear-gradient(135deg, #f8f9fa, #e9ecef)",
                            p: 1,
                          }}
                        >
                          <CardMedia
                            component="img"
                            height="100"
                            image={
                              medicine.image_url || "/medicine-still-life.png"
                            }
                            alt={medicine.name}
                            sx={{
                              objectFit: "contain",
                              width: 100,
                              borderRadius: 1,
                            }}
                          />
                        </Box>

                        {/* Product Details */}
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: "#2c3e50",
                              mb: 1,
                              lineHeight: 1.3,
                            }}
                          >
                            {medicine.name}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            {medicine.company_name || "Generic Medicine"}
                          </Typography>

                          {/* Pricing */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 2,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{ color: "#008080", fontWeight: 700 }}
                            >
                              ₹{medicine.ptr}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                textDecoration: "line-through",
                                color: "text.secondary",
                              }}
                            >
                              ₹{medicine.mrp}
                            </Typography>
                            <Chip
                              label={`${Math.round(
                                ((medicine.mrp - medicine.ptr) / medicine.mrp) *
                                  100
                              )}% OFF`}
                              size="small"
                              sx={{
                                background:
                                  "linear-gradient(45deg, #ff6b6b, #ee5a24)",
                                color: "white",
                                fontWeight: 600,
                              }}
                            />
                          </Box>

                          <Typography variant="body2" color="text.secondary">
                            GST: {medicine.gst || "18"}% • Amount: ₹
                            {(medicine.quantity * medicine.ptr).toFixed(2)}
                          </Typography>
                        </Box>

                        {/* Quantity Controls */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <IconButton
                              onClick={() =>
                                handleQuantityChange(
                                  medicine.medicine_id,
                                  medicine.quantity - 1
                                )
                              }
                              size="small"
                              sx={{
                                background:
                                  "linear-gradient(45deg, #008080, #20B2AA)",
                                color: "white",
                                "&:hover": {
                                  background:
                                    "linear-gradient(45deg, #006666, #1a9999)",
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <RemoveIcon />
                            </IconButton>

                            <TextField
                              value={medicine.quantity}
                              onChange={(e) =>
                                handleQuantityChange(
                                  medicine.medicine_id,
                                  Number.parseInt(e.target.value) || 1
                                )
                              }
                              type="number"
                              inputProps={{
                                min: 1,
                                max: 999,
                                style: { textAlign: "center", fontWeight: 600 },
                              }}
                              sx={{
                                width: "70px",
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  "&:hover fieldset": {
                                    borderColor: "#008080",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#008080",
                                  },
                                },
                              }}
                            />

                            <IconButton
                              onClick={() =>
                                handleQuantityChange(
                                  medicine.medicine_id,
                                  medicine.quantity + 1
                                )
                              }
                              size="small"
                              sx={{
                                background:
                                  "linear-gradient(45deg, #008080, #20B2AA)",
                                color: "white",
                                "&:hover": {
                                  background:
                                    "linear-gradient(45deg, #006666, #1a9999)",
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>

                          <IconButton
                            onClick={() => handleRemove(medicine.medicine_id)}
                            sx={{
                              color: "#e74c3c",
                              "&:hover": {
                                background: "rgba(231, 76, 60, 0.1)",
                                transform: "scale(1.1)",
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Grid>

            {/* Order Summary */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 3,
                  position: "sticky",
                  top: 20,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, color: "#2c3e50" }}
                  >
                    Order Summary
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">
                      Subtotal ({cartItems.length} items)
                    </Typography>
                    <Typography variant="body2">
                      ₹{calculateSubtotal().toFixed(2)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">GST (18%)</Typography>
                    <Typography variant="body2">
                      ₹{calculateTax().toFixed(2)}
                    </Typography>
                  </Box>

                  {calculateSavings() > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#27ae60" }}>
                        You Save
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#27ae60", fontWeight: 600 }}
                      >
                        ₹{calculateSavings().toFixed(2)}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 3,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Total
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "#008080" }}
                    >
                      ₹{calculateTotal()}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleCheckout}
                    sx={{
                      background: "linear-gradient(45deg, #008080, #20B2AA)",
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: "1.1rem",
                      mb: 2,
                      "&:hover": {
                        background: "linear-gradient(45deg, #006666, #1a9999)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(0, 128, 128, 0.4)",
                      },
                    }}
                  >
                    Proceed to Checkout
                  </Button>

                  {/* Trust Badges */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-around",
                      mt: 3,
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <SecurityIcon sx={{ color: "#008080", mb: 1 }} />
                      <Typography variant="caption" display="block">
                        Secure Payment
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <LocalShippingIcon sx={{ color: "#008080", mb: 1 }} />
                      <Typography variant="caption" display="block">
                        Fast Delivery
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <LocalOfferIcon sx={{ color: "#008080", mb: 1 }} />
                      <Typography variant="caption" display="block">
                        Best Prices
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
        />
      </Container>
    </Box>
  );
};

export default CartCom;
